import { Bell, ChevronDown, LogOut } from "lucide-react";
import { observer } from "mobx-react";
import { useUser } from "@clerk/clerk-react";
import { useNavigate } from "react-router";
import { useMemo } from "react";
import { APP_TO_BUILD } from "../../../appsConfig";
import { useRootStore } from "@/state/RootStore";
import { useLogout } from "@/hooks/useLogout";
import { getExamDisplayNames } from "@/utils/examSelection";
import { canShowChangeExamType } from "@/utils/examTypeNavigation";
import { ExamSwitcherPopover } from "./ExamSwitcherPopover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";

export const AppHeader = observer(() => {
  const store = useRootStore();
  const navigate = useNavigate();
  const { user } = useUser();
  const { handleSignOut } = useLogout();
  const { t } = useTranslation();
  const stateCode = store.mainAppStore.selectedStateCode || "";
  const rawStateName = store.questionsStore.stateInfo?.stateNames?.[stateCode] || t("study.select_exam");
  const codebookYear = store.questionsStore.getEffectiveCodebookYear(stateCode);
  const examName = store.questionsStore.stateInfo?.stateExamNames?.[stateCode] || "";
  const displayNames = APP_TO_BUILD === "electrical_us"
    ? getExamDisplayNames(examName, rawStateName)
    : { examLabel: examName, stateName: rawStateName };
  const stateName = displayNames.stateName;
  const fullName = user?.fullName || store.mainAppStore.profileName || t("study.student_fallback");
  const email = user?.primaryEmailAddress?.emailAddress || store.mainAppStore.profileEmail || "";
  const initials = useMemo(
    () => fullName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase(),
    [fullName],
  );
  const subtitle = APP_TO_BUILD === "electrical_us"
    ? `${displayNames.examLabel || t("study.exam_fallback")} - NEC ${codebookYear || ""}`
    : examName;
  const canChangeExamType = canShowChangeExamType(APP_TO_BUILD);

  return (
    <header className="flex h-[72px] w-full shrink-0 items-center justify-between gap-2 border-b border-[#dddfe4] bg-white px-3 sm:gap-4 sm:px-6">
      <div className="min-w-0 flex-1">
        {canChangeExamType ? (
          <ExamSwitcherPopover stateName={stateName} subtitle={subtitle} />
        ) : (
          <div className="min-w-0">
            <p className="truncate text-base font-semibold leading-6 text-[#0a0c11] sm:text-lg sm:leading-[27px]">{t("study.ibew_header_title")}</p>
            <p className="hidden truncate text-sm leading-[21px] tracking-[0.07px] text-[#5b616d] sm:block">{t("study.ibew_header_subtitle")}</p>
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2 sm:gap-4 lg:gap-6">
        <button aria-label={t("common.notifications")} className="rounded-full text-[#5b616d] outline-none focus-visible:ring-2 focus-visible:ring-[var(--dp-brand-button)] focus-visible:ring-offset-2" onClick={() => navigate("/profile/notification-settings")} type="button">
          <Bell aria-hidden="true" className="h-6 w-6" />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="group flex h-[50px] items-center gap-2.5 rounded-full p-1.5 sm:pl-2.5 sm:pr-3" data-testid="account-menu-trigger" type="button">
              <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-[var(--dp-brand-deep)] text-xs font-semibold text-white">
                {initials || "S"}
              </span>
              <span className="hidden max-w-[140px] truncate text-sm font-semibold leading-[21px] tracking-[0.07px] text-[#0a0c11] md:block">{fullName}</span>
              <ChevronDown aria-hidden="true" className="hidden h-5 w-5 shrink-0 transition-transform group-data-[state=open]:rotate-180 sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={4}
            className="w-[264px] rounded-2xl border-0 bg-white p-4 shadow-[0_4px_6px_-2px_rgba(0,0,0,0.05),0_10px_15px_-3px_rgba(0,0,0,0.10)]"
          >
            <DropdownMenuItem
              className="cursor-pointer gap-3 rounded-xl p-0 focus:bg-[#f4f4f6]"
              data-testid="account-menu-profile"
              onSelect={() => navigate("/profile")}
            >
              <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-[var(--dp-brand-deep)] text-xs font-semibold text-white">
                {initials || "S"}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold leading-[21px] text-[#0a0c11]">{fullName}</span>
                <span className="block truncate text-xs font-medium leading-[18px] text-[#5b616d]">{email}</span>
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="mx-0 my-4 bg-[#dddfe4]" />
            <DropdownMenuItem
              className="cursor-pointer gap-3 rounded-lg p-0 text-sm font-medium leading-[21px] text-[#c20000] focus:bg-[#fff1f1] focus:text-[#c20000]"
              onSelect={() => void handleSignOut()}
            >
              <LogOut aria-hidden="true" className="h-5 w-5" />
              {t("auth.sign_out")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
});
